/**
 * Page Justifications : le surveillant valide ou refuse les justifications
 * d'absence/retard déclarées par les parents depuis l'app.
 */
import React, { useState } from 'react';
import { CheckCircle2, XCircle, FileCheck, Loader2 } from 'lucide-react';
import {
  useListerJustificationsQuery, useTraiterJustificationMutation,
} from '../../services/vieScolaireApi';

export default function Justifications() {
  const [statut, setStatut] = useState<'en_attente' | 'validees' | 'toutes'>('en_attente');
  const { data, isFetching, refetch } = useListerJustificationsQuery({ statut });
  const [traiter, { isLoading }] = useTraiterJustificationMutation();
  const [modalRejet, setModalRejet] = useState<any | null>(null);

  const handleValider = async (id: number) => {
    try {
      await traiter({ id, action: 'valider' }).unwrap();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileCheck size={24} /> Justifications d'absence
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Absences et retards déclarés par les parents — à valider ou refuser.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        {([
          { v: 'en_attente', label: `En attente${(data?.nb_en_attente ?? 0) > 0 ? ` (${data?.nb_en_attente})` : ''}` },
          { v: 'validees', label: 'Validées' },
          { v: 'toutes', label: 'Toutes' },
        ] as const).map(o => (
          <button key={o.v} onClick={() => setStatut(o.v)}
            className={`px-3 py-1.5 text-xs rounded ${
              statut === o.v
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}>
            {o.label}
          </button>
        ))}
        <button onClick={() => refetch()} className="ml-auto px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
          {isFetching ? '…' : '↻ Actualiser'}
        </button>
      </div>

      {!isFetching && data?.results.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center text-sm text-gray-500">
          Aucune justification dans cette catégorie.
        </div>
      )}

      <div className="space-y-3">
        {data?.results.map(j => (
          <div key={j.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {j.matricule_etudiant} • {j.seance.classe}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Déclaré par : {j.saisi_par_nom || '—'}
                  {j.date_saisie && ` • ${new Date(j.date_saisie).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded ${
                j.statut === 'absent' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : j.statut === 'retard' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              }`}>
                {j.statut === 'absent' ? 'Absence' : j.statut === 'retard' ? `Retard ${j.minutes_retard}min` : 'Justifiée'}
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-3 mb-3">
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Séance concernée</div>
              <div className="text-sm text-gray-900 dark:text-white">
                {j.seance.libelle_matiere} — {new Date(j.seance.date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'short' })}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {j.seance.heure_debut}–{j.seance.heure_fin}
                {j.seance.formateur && ` • ${j.seance.formateur}`}
              </div>
            </div>

            {j.motif && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 px-3 py-2 mb-3 rounded">
                <div className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 uppercase mb-1">Motif du parent</div>
                <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{j.motif}</div>
              </div>
            )}

            {!j.validee && (
              <div className="flex gap-2">
                <button onClick={() => handleValider(j.id)} disabled={isLoading}
                  className="flex-1 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded inline-flex items-center justify-center gap-2">
                  <CheckCircle2 size={14} /> Accepter la justification
                </button>
                <button onClick={() => setModalRejet(j)}
                  className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded inline-flex items-center gap-1">
                  <XCircle size={14} /> Refuser
                </button>
              </div>
            )}
            {j.validee && (
              <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle2 size={13} /> Justification acceptée
              </div>
            )}
          </div>
        ))}
      </div>

      {modalRejet && (
        <ModalRejet justification={modalRejet} onClose={() => setModalRejet(null)} />
      )}
    </div>
  );
}

function ModalRejet({ justification, onClose }: { justification: any; onClose: () => void }) {
  const [commentaire, setCommentaire] = useState('');
  const [traiter, { isLoading }] = useTraiterJustificationMutation();

  const handle = async () => {
    try {
      await traiter({ id: justification.id, action: 'rejeter', commentaire }).unwrap();
      onClose();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-bold text-red-600 dark:text-red-400">Refuser la justification</h3>
        </header>
        <div className="p-5 space-y-3">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Refuser la justification pour <strong>{justification.matricule_etudiant}</strong>{' '}
            ({justification.seance.libelle_matiere}, {new Date(justification.seance.date).toLocaleDateString('fr-FR')}) ?
          </div>
          <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
            rows={3} maxLength={300} placeholder="Commentaire (facultatif, transmis au parent)"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button onClick={handle} disabled={isLoading}
            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            Refuser
          </button>
        </footer>
      </div>
    </div>
  );
}
